import React, { useRef, useState, useEffect } from 'react';
import './DetailRecolteur.css';
import { getRecolteurStats } from '../services/api';

const DetailRecolteur = ({ recolteur, stats: initialStats, membres: initialMembres, onClose, recolteurId }) => {
  const printRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [stats, setStats] = useState(initialStats);
  const [membres, setMembres] = useState(initialMembres);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialStats) {
      setStats(initialStats);
    }
    if (initialMembres) {
      setMembres(initialMembres);
    }
  }, [initialStats, initialMembres]);

  const handleDateChange = async (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    
    if (!recolteurId) return;
    
    setLoading(true);
    try {
      const response = await getRecolteurStats(recolteurId, newDate);
      if (response && response.data) {
        setStats(response.data.statistiques);
        setMembres(response.data.membres || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!recolteur || !stats) return null;

  return (
    <div className="detail-recolteur-overlay" onClick={onClose}>
      <div className="detail-recolteur-modal" onClick={(e) => e.stopPropagation()}>
        <div className="detail-recolteur-header">
          <h2>Détails du récolteur: {recolteur.nom_complet}</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', color: '#666' }}>Filtrer par date:</label>
              <input
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                style={{
                  padding: '5px 10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                disabled={loading}
              />
            </div>
            <button className="btn btn-secondary" onClick={onClose}>
              ✕ Fermer
            </button>
          </div>
        </div>

        <div className="detail-recolteur-content">
          <div className="print-content" ref={printRef} style={{ display: 'block', visibility: 'visible', background: 'white' }}>
            {/* En-tête pour l'impression */}
            <div className="print-header">
              <div className="header-content-wrapper">
                <div className="header-logo">
                  <img src="/logo.png" alt="Logo" className="logo-img" />
                </div>
                <div className="header-text">
                  <h1>MUTU NDE ESPOIR ASBL</h1>
                  <h2>Rapport du Récolteur: {recolteur.nom_complet}</h2>
                  <div className="header-info">
                    <p className="print-date">
                      <strong>Date d'édition :</strong> {new Date().toLocaleDateString('fr-FR', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Informations du récolteur - Format tableau */}
            <div className="print-table-container" style={{ marginBottom: '30px' }}>
              <table className="print-table">
                <thead>
                  <tr>
                    <th colSpan="2">Informations du Récolteur</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ width: '30%', fontWeight: 'bold' }}>Nom complet</td>
                    <td>{recolteur.nom_complet}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Username</td>
                    <td>{recolteur.username}</td>
                  </tr>
                  {recolteur.matricule && (
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Matricule</td>
                      <td>{recolteur.matricule}</td>
                    </tr>
                  )}
                  {recolteur.association && (
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Association</td>
                      <td>{recolteur.association}</td>
                    </tr>
                  )}
                  {recolteur.commune && (
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Commune</td>
                      <td>{recolteur.commune}</td>
                    </tr>
                  )}
                  {recolteur.telephone && (
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Téléphone</td>
                      <td>{recolteur.telephone}</td>
                    </tr>
                  )}
                  {recolteur.centre_sante && (
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Centre de santé</td>
                      <td>{recolteur.centre_sante}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Statistiques journalières - Format tableau */}
            <div className="print-table-container" style={{ marginBottom: '30px' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p>Chargement des statistiques...</p>
                </div>
              ) : (
                <table className="print-table">
                  <thead>
                    <tr>
                      <th colSpan="2">Statistiques Journalières {selectedDate ? `(${new Date(selectedDate).toLocaleDateString('fr-FR')})` : ''}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ width: '50%', fontWeight: 'bold' }}>Membres inscrits {selectedDate ? 'le ' + new Date(selectedDate).toLocaleDateString('fr-FR') : "aujourd'hui"}</td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{stats.journalieres.membresAujourdhui || 0}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Montant collecté {selectedDate ? 'le ' + new Date(selectedDate).toLocaleDateString('fr-FR') : "aujourd'hui"}</td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {parseFloat(stats.journalieres.montantRecolteAujourdhui || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Nombre de paiements</td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{stats.journalieres.nombrePaiementsAujourdhui || 0}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Cotisations payées {selectedDate ? 'le ' + new Date(selectedDate).toLocaleDateString('fr-FR') : "aujourd'hui"}</td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{stats.journalieres.cotisationsPayeesAujourdhui || 0}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Cartes payées {selectedDate ? 'le ' + new Date(selectedDate).toLocaleDateString('fr-FR') : "aujourd'hui"}</td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{stats.journalieres.cartesPayeesAujourdhui || 0}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Montant cartes {selectedDate ? 'le ' + new Date(selectedDate).toLocaleDateString('fr-FR') : "aujourd'hui"}</td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {parseFloat(stats.journalieres.montantCartesAujourdhui || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                      </td>
                    </tr>
                    {stats.journalieres.date && (
                      <tr>
                        <td style={{ fontWeight: 'bold' }}>Date</td>
                        <td>{new Date(stats.journalieres.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Statistiques globales - Format tableau */}
            <div className="print-table-container" style={{ marginBottom: '30px' }}>
              <table className="print-table">
                <thead>
                  <tr>
                    <th colSpan="2">Statistiques Globales</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ width: '50%', fontWeight: 'bold' }}>Total Membres</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{stats.globales.totalMembres || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Total Montant Récolté</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                      {parseFloat(stats.globales.totalMontantRecolte || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                    </td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Total Paiements</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{stats.globales.totalPaiements || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Cotisations Payées</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#28a745' }}>{stats.globales.cotisationsPayees || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Cotisations Impayées</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#dc3545' }}>{stats.globales.cotisationsImpayees || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Total Cartes Payées</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#17a2b8' }}>{stats.globales.totalCartesPayees || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Montant Total Cartes</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#17a2b8' }}>
                      {parseFloat(stats.globales.montantTotalCartes || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Liste des membres */}
            <div className="print-table-container">
              {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p>Chargement des membres...</p>
                </div>
              ) : (
                <table className="print-table">
                  <thead>
                    <tr>
                      <th>N°</th>
                      <th>Matricule</th>
                      <th>Nom complet</th>
                      <th>Téléphone</th>
                      <th>Date d'adhésion</th>
                      <th>Commune</th>
                      <th>Quartier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {membres && membres.length > 0 ? (
                      membres.map((membre, index) => (
                        <tr key={membre.id}>
                          <td>{index + 1}</td>
                          <td>{membre.numero_matricule || '-'}</td>
                          <td>
                            {membre.nom} {membre.postnom || ''} {membre.prenom}
                          </td>
                          <td>{membre.telephone || '-'}</td>
                          <td>
                            {membre.date_adhesion ? new Date(membre.date_adhesion).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td>{membre.commune || '-'}</td>
                          <td>{membre.quartier || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                          {selectedDate ? `Aucun membre enregistré par ce récolteur le ${new Date(selectedDate).toLocaleDateString('fr-FR')}` : 'Aucun membre enregistré par ce récolteur'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pied de page pour l'impression */}
            <div className="print-footer">
              <p>Document généré par MUTU NDE ESPOIR ASBL - Système de gestion</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailRecolteur;

